import React from 'react';
import { useTranslation } from 'next-i18next';
import TruncateMarkup from 'react-truncate-markup';

import Box from '@mui/material/Box';

import Tag from 'src/components/ui/Tag';
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import type { Props as TypographyProps } from 'src/components/ui/Typography';
import Avatar from 'src/components/ui/Avatar';
import Image from 'src/components/ui/Image';
import type { TinyPreviewCardProps } from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';

import { colours } from 'src/constants/colours';
import isNotNull from 'src/utils/isNotNull';
import { getFormattedMessageWithMentions } from 'src/utils/mentionsUtils';

import type { Medium } from 'src/types/__generated__/graphql';
import formatDistanceToNow from 'src/utils/formatDistanceToNow';
import useOwnerSubDisplayName from 'src/hooks/useOwnerSubDisplayName';
import { styledComponent } from 'src/utils/styled';

const TitleAndSubtitleContainer = styledComponent(Box)`
    display: flex;
    flex-direction: column;
    margin: 1rem 0 1rem 1rem;
    justify-content: center;
`;

const StyledTitle = styledComponent(Typography)`
    color: ${colours.secondary.main};
    font-weight: 700;
    font-size: 0.75rem;
    line-height: 0.875rem;
`;

const StyledSubtitle = styledComponent(Typography)`
    font-weight: 700;
    font-size: 0.875rem;
    line-height: 1.125rem;
    margin-top: 0.5rem;
`;

const StyledTag = styledComponent(Tag)`
    height: 1.5rem;
    align-self: flex-start;
    background-color: ${colours.secondary.O08};
    color: ${colours.secondary.main};
    img {
        width: 1rem;
        height: 1rem;
        margin-right: 0.25rem;
    }
    span {
        font-weight: 400;
        font-size: 0.75rem;
        line-height: 0.875rem;
    }
    .MuiChip-icon {
        margin-right: -0.5rem;
        color: currentColor;
    }
`;

const StyledAdDescriptionTypography = styledComponent(Typography)`
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 0.875rem;
`;

const StyledTypography = styledComponent(Typography)`
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 0.875rem;
`;

const StyledWrapBox = styledComponent(Box)`
    overflow-wrap: break-word;
    padding-right: 1rem;
    display: flex;
    flex-direction: column;
    margin-top: 0.75rem;
    margin-left: 1rem;
`;

interface SubtitleMarkupProps extends TypographyProps {
    subtitle: TinyPreviewCardContentProps['subtitle'] | React.ReactNode[];
}

const SubtitleMarkup: React.FC<SubtitleMarkupProps> = ({ subtitle, ...rest }) => (
    <StyledSubtitle {...rest}>
        <TruncateMarkup lines={2}>
            <span>{subtitle}</span>
        </TruncateMarkup>
    </StyledSubtitle>
);

interface TitleAndSubtitleProps {
    title: TinyPreviewCardContentProps['title'];
    subtitle: TinyPreviewCardContentProps['subtitle'];
    titleDataTargetId: string;
    subtitleDataTargetId: string;
}

const TitleAndSubtitle: React.FC<TitleAndSubtitleProps> = ({
    title,
    subtitle,
    titleDataTargetId,
    subtitleDataTargetId,
}) => (
    <>
        <Typography
            data-target-id={titleDataTargetId}
            variant="h5"
            sx={{ color: colours.black.main, lineHeight: '1.25rem' }}
        >
            {title}
        </Typography>
        <StyledTypography data-target-id={subtitleDataTargetId} sx={{ color: colours.black.O60 }}>
            {subtitle}
        </StyledTypography>
    </>
);

interface AdDescriptionItemProps {
    iconName: IconProps['name'];
    text: string;
}

const AdDescriptionItem: React.FC<AdDescriptionItemProps> = ({ iconName, text }) => (
    <Box mr="0.5rem" display="flex" alignItems="center">
        <Icon color={colours.backgroundGray} size="0.75rem" name={iconName} />
        <Box ml="0.25rem">
            <StyledAdDescriptionTypography>{text}</StyledAdDescriptionTypography>
        </Box>
    </Box>
);

export interface TinyPreviewCardContentProps {
    type: TinyPreviewCardProps['type'];
    ad: TinyPreviewCardProps['ad'];
    post: TinyPreviewCardProps['post'];
    user: TinyPreviewCardProps['user'];
    title: TinyPreviewCardProps['title'];
    subtitle: TinyPreviewCardProps['subtitle'];
    tag: TinyPreviewCardProps['tag'];
}

const TinyPreviewCardContent: React.FC<TinyPreviewCardContentProps> = ({
    type,
    title,
    tag,
    ad,
    post,
    subtitle,
    user,
}) => {
    const { t, i18n } = useTranslation(['ad', 'news']);

    const postOwnerDisplayName = useOwnerSubDisplayName(post?.owner ?? undefined);

    switch (type) {
        case 'company':
        case 'pagesJaunesPro':
            return (
                <TitleAndSubtitleContainer>
                    <TitleAndSubtitle
                        title={title}
                        subtitle={subtitle}
                        titleDataTargetId={
                            type === 'pagesJaunesPro'
                                ? 'tinyCardPreview-pagesjaunespro-fullName'
                                : 'tinyCardPreview-company-fullName'
                        }
                        subtitleDataTargetId={
                            type === 'pagesJaunesPro'
                                ? 'tinyCardPreview-pagesjaunespro-occupation'
                                : 'tinyCardPreview-company-occupation'
                        }
                    />
                </TitleAndSubtitleContainer>
            );
        case 'user':
            return (
                <TitleAndSubtitleContainer my="0.75rem !important">
                    <TitleAndSubtitle
                        title={title}
                        subtitle={subtitle}
                        titleDataTargetId="tinyCardPreview-user-fullName"
                        subtitleDataTargetId="tinyCardPreview-user-occupation"
                    />
                    <StyledTypography
                        data-target-id="tinyCardPreview-user-commonRelations"
                        sx={{ color: colours.black.O60 }}
                    >
                        {t('profile:commonRelations', {
                            count: user?.mutualContactsCount ?? 0,
                            plurial: user?.mutualContactsCount && user.mutualContactsCount > 1 ? 's' : '',
                        })}
                    </StyledTypography>
                </TitleAndSubtitleContainer>
            );
        case 'post':
            return (
                <StyledWrapBox sx={!post?.media.length ? { ml: '1.5rem !important' } : undefined}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        {post?.owner && (
                            <Box mr="0.5rem">
                                <Avatar
                                    data-target-id="tinyPreviewCard-avatar"
                                    image={post.owner.image as Medium}
                                    fallbackText={post.owner.initials}
                                    size={32}
                                />
                            </Box>
                        )}
                        <Box display="flex" flexDirection="column">
                            <SubtitleMarkup
                                subtitle={subtitle}
                                data-target-id="tinyPreviewCard-subtitle"
                                sx={{ marginTop: '0rem !important' }}
                            />
                            <Box>
                                {post && (
                                    <SubtitleMarkup
                                        subtitle={`${postOwnerDisplayName} · ${formatDistanceToNow(
                                            post.createdAt as string,
                                            i18n.language as SupportedLangs,
                                        )}`}
                                        data-target-id="tinyPreviewCard-subInfo"
                                        sx={{
                                            marginTop: '0rem !important',
                                            color: colours.blackOpacity.O38,
                                            fontSize: '0.75rem !important',
                                            fontWeight: '400 !important',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    {post?.message && (
                        <SubtitleMarkup
                            subtitle={getFormattedMessageWithMentions(post.message)}
                            data-target-id="tinyPreviewCard-message"
                            style={{
                                color: colours.linkGrey,
                                fontWeight: 'normal',
                                fontSize: '0.85rem',
                                marginBottom: '0.5rem',
                            }}
                        />
                    )}
                </StyledWrapBox>
            );
        case 'newsPost':
            return (
                <StyledWrapBox>
                    <StyledTag
                        data-target-id="tinyPreviewCard-tag"
                        label={i18n.language === 'fr' ? tag?.nameFr : tag?.nameEn}
                        icon={<Image src={tag?.icon ?? ''} alt={tag?.nameFr ?? ''} width={16} height={16} />}
                    />
                    <SubtitleMarkup subtitle={subtitle} data-target-id="tinyPreviewCard-subtitle" />
                </StyledWrapBox>
            );
        case 'ad':
            return (
                <StyledWrapBox>
                    <StyledTitle data-target-id="tinyPreviewCard-title" variant="h4">
                        {title}
                    </StyledTitle>
                    <SubtitleMarkup subtitle={subtitle} data-target-id="tinyPreviewCard-subtitle" />
                    <Box display="flex" flexDirection="column" data-target-id="tinyPreviewCard-adInfo" mt="0.25rem">
                        <Box display="flex" alignItems="center">
                            {isNotNull(ad?.roomNumber) && (
                                <AdDescriptionItem
                                    iconName="Room"
                                    text={t('ad:countRoomsShort', { count: ad?.roomNumber })}
                                />
                            )}
                            {isNotNull(ad?.bedroomNumber) && (
                                <AdDescriptionItem
                                    iconName="BedRoom"
                                    text={t('ad:countBedRoomsShort', { count: ad?.bedroomNumber })}
                                />
                            )}
                            {isNotNull(ad?.livingArea) && (
                                <AdDescriptionItem
                                    iconName="Area"
                                    text={t('ad:livingArea', { livingArea: ad?.livingArea })}
                                />
                            )}
                            {isNotNull(ad?.surfaceArea) && (
                                <AdDescriptionItem
                                    iconName="Area"
                                    text={t('ad:surfaceArea', { surfaceArea: ad?.surfaceArea })}
                                />
                            )}
                        </Box>
                        <Box>
                            <StyledAdDescriptionTypography data-target-id="tinyPreviewCard-location" variant="body2">
                                {ad?.address.zipCode} {ad?.address.cityName}
                            </StyledAdDescriptionTypography>
                        </Box>
                    </Box>
                </StyledWrapBox>
            );
        default:
            return (
                <StyledWrapBox>
                    <StyledTitle data-target-id="tinyPreviewCard-title" variant="h4">
                        {title}
                    </StyledTitle>
                    <SubtitleMarkup subtitle={subtitle} data-target-id="tinyPreviewCard-subtitle" />
                </StyledWrapBox>
            );
    }
};

export default TinyPreviewCardContent;
